import {
  availabilityOutcome,
  blockedOutcome,
  bookedOutcome,
} from "../core/outcomes";
import type {
  AvailabilityToolResult,
  BookingToolResult,
  Planner,
  PlannerAction,
  PlannerContext,
} from "../core/types";

function latestAvailability(
  context: PlannerContext,
): AvailabilityToolResult | undefined {
  return context.toolResults
    .filter(
      (result): result is AvailabilityToolResult =>
        result.name === "find_slots",
    )
    .at(-1);
}

function latestBooking(
  context: PlannerContext,
): BookingToolResult | undefined {
  return context.toolResults
    .filter(
      (result): result is BookingToolResult =>
        result.name === "book_appointment",
    )
    .at(-1);
}

export class SyntheticPlanner implements Planner {
  async next(context: PlannerContext): Promise<PlannerAction> {
    const booking = latestBooking(context);
    if (booking) {
      if (
        (booking.status === "booked" || booking.status === "replayed") &&
        booking.booking
      ) {
        return {
          type: "respond",
          reply:
            booking.status === "replayed"
              ? "The same confirmed action was safely replayed without a second mutation."
              : "The synthetic appointment was confirmed after the guard allowed the mutation.",
          outcome: bookedOutcome(
            booking.booking,
            booking.status === "replayed",
          ),
        };
      }

      return {
        type: "respond",
        reply: `The mutation was not executed: ${booking.reason ?? "unknown_reason"}.`,
        outcome: blockedOutcome(
          booking.reason ?? "adapter_rejected_mutation",
        ),
      };
    }

    const availability = latestAvailability(context);
    if (!availability) {
      return {
        type: "tool_call",
        call: {
          id: "tool-find-slots",
          name: "find_slots",
          input: {},
        },
      };
    }

    if (!context.request.requestedSlot) {
      return {
        type: "respond",
        reply: `Available synthetic slots: ${availability.snapshot.slots.join(", ")}.`,
        outcome: availabilityOutcome(availability.snapshot),
      };
    }

    return {
      type: "tool_call",
      call: {
        id: "tool-book-appointment",
        name: "book_appointment",
        input: {
          sessionId: context.request.sessionId,
          slot: context.request.requestedSlot,
          expectedVersion: availability.snapshot.version,
          idempotencyKey: context.request.idempotencyKey,
        },
      },
    };
  }
}
